import { ReactNode, useEffect, useId } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, IconButton, Popover, Typography } from '@mui/material';
import type { IconButtonProps, SxProps } from '@mui/material';
import { useThemeProps } from '@mui/material/styles';
import { get } from 'lodash';
import { InfoPopoverGroupHandle, useInfoPopoverGroupContext, useInfoPopoverZone } from './InfoPopoverGroup';

export type InfoPopoverProps = {
  anchorEl: Element | null;
  setAnchorEl: (el: HTMLElement | null) => void;
  title?: string;
  icon?: ReactNode;
  group?: InfoPopoverGroupHandle;
  children?: ReactNode;
  dismissOnOutsideClick?: boolean;
  anchorOrigin?: {
    vertical: 'top' | 'bottom' | 'center';
    horizontal: 'left' | 'center' | 'right';
  };
  transformOrigin?: {
    vertical: 'top' | 'bottom' | 'center';
    horizontal: 'left' | 'center' | 'right';
  };
};

function getArrowStyles(
  transformOrigin: InfoPopoverProps['transformOrigin'],
  bgColor: string
): Record<string, unknown> {
  const vertical = transformOrigin?.vertical ?? 'bottom';
  const horizontal = transformOrigin?.horizontal ?? 'center';
  const size = 10;

  const horizontalPos =
    horizontal === 'center'
      ? { left: `calc(50% - ${size / 2}px)` }
      : horizontal === 'left'
        ? { left: 8 }
        : { right: 8 };

  const base = {
    ...horizontalPos,
    width: size,
    height: size,
    backgroundColor: bgColor,
    transform: 'rotate(45deg)',
    zIndex: -1,
  };

  if (vertical === 'bottom') {
    return { ...base, bottom: -4 };
  }

  if (vertical === 'top') {
    return { ...base, top: -4 };
  }

  if (vertical === 'center') {
    if (horizontal === 'right') {
      return { ...base, right: -4, left: 'auto', top: `calc(50% - ${size / 2}px)` };
    }
    if (horizontal === 'left') {
      return { ...base, left: -4, right: 'auto', top: `calc(50% - ${size / 2}px)` };
    }
  }

  return {};
}

export function InfoPopover(inProps: InfoPopoverProps) {
  type ThemedProps = InfoPopoverProps & {
    minWidth?: number | string;
    backgroundColor?: string;
    titleSx?: SxProps;
    closeButtonProps?: Partial<Omit<IconButtonProps, 'onClick'>> & { iconSx?: IconButtonProps['sx'] };
  };
  const {
    titleSx = {},
    minWidth = 280,
    backgroundColor = 'background.paper',
    closeButtonProps,
    ...props
  } = useThemeProps({
    props: inProps,
    name: 'InfoPopover',
  }) as ThemedProps;
  const { sx: closeButtonSx, iconSx, ...restCloseButtonProps } = closeButtonProps ?? {};
  const {
    anchorEl,
    setAnchorEl,
    title,
    icon,
    group: groupProp,
    children,
    dismissOnOutsideClick = false,
    anchorOrigin = { vertical: 'top', horizontal: 'center' },
    transformOrigin = { vertical: 'bottom', horizontal: 'center' },
  } = props;
  const id = useId();
  const contextGroup = useInfoPopoverGroupContext();
  const group = groupProp ?? contextGroup;
  const zone = useInfoPopoverZone();

  useEffect(() => {
    if (!group) {
      return;
    }
    return group.subscribe(id, () => setAnchorEl(null), zone);
  }, [group, id, setAnchorEl, zone]);

  useEffect(() => {
    if (anchorEl && group) {
      group.notifyOpen(id, zone);
    }
  }, [anchorEl, group, id, zone]);

  useEffect(() => {
    const handleScroll = () => {
      if (anchorEl) {
        setAnchorEl(null);
      }
    };
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [anchorEl]);

  return (
    <Popover
      elevation={0}
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' && !dismissOnOutsideClick) {
          return;
        }
        setAnchorEl(null);
      }}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        root: dismissOnOutsideClick ? undefined : { style: { pointerEvents: 'none' } },
        paper: {
          sx: (theme) => {
            const bgColor = get(theme.palette, backgroundColor) as string;
            const vertical = transformOrigin?.vertical ?? 'bottom';
            const horizontal = transformOrigin?.horizontal ?? 'center';
            const gapMargin = {
              ...(vertical === 'bottom' && { mt: '-12px' }),
              ...(vertical === 'top' && { mt: '12px' }),
              ...(horizontal === 'right' && { ml: '-12px' }),
              ...(horizontal === 'left' && { ml: '12px' }),
            };
            return {
              ...gapMargin,
              padding: '16px 16px 12px 16px',
              pointerEvents: 'auto',
              backgroundColor: bgColor,
              overflow: 'visible',
              filter: 'drop-shadow(0px 3px 8px rgba(0,0,0,0.15)) drop-shadow(0px 1px 3px rgba(0,0,0,0.08))',
              '&::after': {
                content: '""',
                display: 'block',
                position: 'absolute',
                ...getArrowStyles(transformOrigin, bgColor),
              },
            };
          },
        },
      }}
    >
      <Box sx={{ minWidth }}>
        {title && (
          <>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{ mt: -0.5, mb: 1, ...titleSx }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                <Typography variant="subtitle2">{title}</Typography>
              </Box>
              <IconButton
                sx={{ position: 'absolute', right: 0, ...closeButtonSx }}
                {...restCloseButtonProps}
                onClick={() => setAnchorEl(null)}
              >
                <CloseIcon sx={{ color: 'text.secondary', ...iconSx }} />
              </IconButton>
            </Box>
            <Divider />
          </>
        )}
        {children}
      </Box>
    </Popover>
  );
}
